// authService - Authentication API client
// Implements US-1.1: User Authentication with Development Mode
// Implements US-1.2: Viewer Guest Access

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * User data structure returned from auth endpoints
 */
export interface User {
  id: string;
  displayName: string;
  email: string;
}

/**
 * Test user structure for dev mode
 */
export interface TestUser {
  id: string;
  displayName: string;
}

/**
 * Authentication response structure
 */
interface AuthResponse {
  success: boolean;
  token?: string;
  user?: User;
  error?: string;
}

/**
 * Workspace data structure for viewer authentication
 */
export interface ViewerWorkspace {
  id: string;
  name: string;
  roomCode: string;
  framework: {
    id: string;
    name: string;
    slug: string;
  };
  createdAt: string;
  status: string;
}

/**
 * Viewer session data
 */
export interface ViewerSession {
  sessionId: string;
  name: string;
}

/**
 * Viewer authentication response structure
 */
export interface ViewerAuthResponse {
  success: boolean;
  token?: string;
  workspace?: ViewerWorkspace;
  viewer?: ViewerSession;
  error?: string;
}

/**
 * Test users list response
 */
interface TestUsersResponse {
  users: TestUser[];
}

/**
 * FETCH list of test users for dev mode
 * Used to populate test user selector in login UI
 *
 * @returns Array of test users
 * @throws Error if request fails
 */
export async function getTestUsers(): Promise<TestUser[]> {
  try {
    const response = await axios.get<TestUsersResponse>(`${API_URL}/api/auth/dev/users`);
    return response.data.users;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'Failed to fetch test users');
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * LOGIN with dev mode (test user selection)
 * No password required, generates JWT token
 *
 * @param testUserId - ID of selected test user
 * @returns Authentication response with token and user data
 * @throws Error if login fails
 */
export async function loginDev(testUserId: string): Promise<AuthResponse> {
  try {
    const response = await axios.post<AuthResponse>(`${API_URL}/api/auth/dev`, {
      testUserId,
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'Dev mode login failed');
    }
    throw new Error('An unexpected error occurred during login');
  }
}

/**
 * LOGIN with SSO (production mode - Keycloak)
 * Validates Keycloak token from Toolbox and creates 8-hour session
 * Implements SSO integration with insur_tale Toolbox
 *
 * @param keycloakToken - JWT token from Keycloak (passed from Toolbox)
 * @returns Authentication response with session token and user data
 * @throws Error if SSO login fails or token invalid
 */
export async function loginSSO(keycloakToken: string): Promise<AuthResponse> {
  try {
    const response = await axios.post<AuthResponse>(`${API_URL}/api/auth/validate-token`, {
      token: keycloakToken,
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'SSO login failed');
    }
    throw new Error('An unexpected error occurred during SSO login');
  }
}

/**
 * VERIFY current user's authentication
 * Checks if stored token is still valid
 *
 * @param token - JWT token to verify
 * @returns User data if token valid
 * @throws Error if token invalid or expired
 */
export async function verifyToken(token: string): Promise<User> {
  try {
    const response = await axios.get<{ success: boolean; user: User }>(`${API_URL}/api/auth/verify`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.user;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'Token verification failed');
    }
    throw new Error('An unexpected error occurred during verification');
  }
}

/**
 * LOGIN as viewer (guest) with room code and display name
 * Creates ephemeral viewer session without full account
 *
 * @param roomCode - Workspace room code to join
 * @param viewerName - Display name for viewer
 * @returns Viewer authentication response with token and workspace data
 * @throws Error if room code invalid, name duplicate, or workspace closed
 */
export async function loginViewer(roomCode: string, viewerName: string): Promise<ViewerAuthResponse> {
  try {
    const response = await axios.post<ViewerAuthResponse>(`${API_URL}/api/auth/viewer`, {
      roomCode,
      viewerName,
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'Failed to join workspace');
    }
    throw new Error('An unexpected error occurred while joining workspace');
  }
}
